import { alpha, darken, decomposeColor, getContrastRatio, hslToRgb, lighten } from '@mui/material/styles'
import type { Palette, Theme } from '@mui/material/styles'

type RGB = readonly [number, number, number]
type RGBA = readonly [number, number, number, number]

const CACHE_LIMIT = 256

// Cada caché es FIFO y acotada: conserva las entradas recientes sin vaciarse de golpe.
const remember = <T>(cache: Map<string, T>, key: string, value: T): T => {
  if (cache.size >= CACHE_LIMIT) {
    const oldest = cache.keys().next()
    if (!oldest.done) cache.delete(oldest.value)
  }
  cache.set(key, value)
  return value
}

const parsedColorCache = new Map<string, RGBA>()

export interface AdvancedColorStates {
  softBgColor: string
  softTextColor: string
  strongBgColor: string
  strongTextColor: string
  hoverBgColor: string
  faintBgColor: string
  borderColor: string
  borderSoft: string
  borderMedium: string
  borderStrong: string
}

export interface ColorStyles {
  bgColor: string
  textColor: string
  strongBgColor: string
  strongTextColor: string
  borderColor: string
  borderSoft: string
  borderMedium: string
  borderStrong: string
  hoverColor: string
  stripedColor: string
}

export type StyledPaletteColors = {
  [K in keyof Palette]: Palette[K] extends { main: string } ? K : never
}[keyof Palette]

export const COLOR_TINTS = {
  soft: { light: 12, dark: 14 },
  strong: { light: 20, dark: 24 },
} as const

const clamp = (value: number, max = 1) => Math.max(0, Math.min(max, value))
const toHex = (rgb: RGB): string =>
  `#${rgb.map((value) => Math.round(clamp(value, 255)).toString(16).padStart(2, '0')).join('')}`

// MUI procesa los colores concretos. Las expresiones CSS se resuelven aparte.
const parseColor = (input: string): RGBA => {
  const cached = parsedColorCache.get(input)
  if (cached) return cached
  let value = input.trim().toLowerCase()
  if (value === 'transparent') return [0, 0, 0, 0]
  if (value === 'white') value = '#ffffff'
  if (value === 'black') value = '#000000'

  // Normaliza la sintaxis RGB moderna a la sintaxis numérica que procesa MUI.
  const rgb = /^rgba?\(([^()]+)\)$/.exec(value)
  if (rgb) {
    const parts = rgb[1].trim().split(/[\s,/]+/)
    if (parts.length < 3 || parts.length > 4 || parts.some((part) => !/^[+-]?(?:\d*\.)?\d+%?$/.test(part))) {
      throw new Error(`Color RGB no calculable: ${input}`)
    }
    const channels = parts.map((part, index) =>
      part.endsWith('%') ? (parseFloat(part) / 100) * (index < 3 ? 255 : 1) : parseFloat(part),
    )
    value = `${parts.length === 4 ? 'rgba' : 'rgb'}(${channels.join(', ')})`
  }

  const decomposed = decomposeColor(value)
  const result =
    decomposed.type === 'hsl' || decomposed.type === 'hsla' ? decomposeColor(hslToRgb(value)) : decomposed
  if (result.type !== 'rgb' && result.type !== 'rgba') {
    throw new Error(`Se requiere un color sRGB concreto: ${input}`)
  }
  const [r, g, b, a = 1] = result.values
  if (![r, g, b, a].every(Number.isFinite)) throw new Error(`Color inválido: ${input}`)
  return remember(
    parsedColorCache,
    input,
    Object.freeze([clamp(r, 255), clamp(g, 255), clamp(b, 255), clamp(a)] as const),
  )
}

const opaque = (color: string): RGB => {
  const [r, g, b, a] = parseColor(color)
  if (a !== 1) throw new Error(`Se esperaba un color opaco: ${color}`)
  return [r, g, b]
}

const blend = (foreground: RGB, background: RGB, amount: number): RGB => [
  foreground[0] * amount + background[0] * (1 - amount),
  foreground[1] * amount + background[1] * (1 - amount),
  foreground[2] * amount + background[2] * (1 - amount),
]

export const compositeColor = (foreground: string, background: string): string => {
  const [r, g, b, a] = parseColor(foreground)
  return toHex(blend([r, g, b], opaque(background), a))
}

export const mixColors = (foreground: string, background: string, amount: number): string =>
  toHex(blend(opaque(foreground), opaque(background), clamp(amount)))

export const withAlpha = (color: string, opacity: number): string =>
  alpha(toHex(opaque(color)), clamp(opacity))

// Calcula el contraste sobre el color final, incluyendo transparencia del texto.
export const contrastRatio = (foreground: string, background: string): number =>
  getContrastRatio(compositeColor(foreground, background), toHex(opaque(background)))

const fitContrast = (
  color: string,
  backgrounds: readonly string[],
  isDark: boolean,
  minRatio: number,
): string => {
  if (!backgrounds.length || !Number.isFinite(minRatio) || minRatio < 1 || minRatio > 21) {
    throw new Error('Fondos o contraste objetivo inválidos')
  }
  const seed = toHex(opaque(color))
  // MUI redondea la luminancia: deja margen para no quedar justo debajo del mínimo.
  const targetRatio = Math.min(21, minRatio + 0.05)
  const passes = (candidate: string) =>
    backgrounds.every((background) => contrastRatio(candidate, background) >= targetRatio)
  if (passes(seed)) return seed

  const target = isDark ? '#ffffff' : '#000000'
  if (!passes(target)) throw new Error('El contraste solicitado no es posible en esta dirección')

  let low = 0
  let high = 1
  let result = target
  for (let iteration = 0; iteration < 20; iteration += 1) {
    const amount = (low + high) / 2
    const candidate = toHex(opaque(isDark ? lighten(seed, amount) : darken(seed, amount)))
    if (passes(candidate)) {
      high = amount
      result = candidate
    } else {
      low = amount
    }
  }
  return result
}

export const ensureContrast = (color: string, background: string, isDark: boolean, minRatio = 4.5): string =>
  fitContrast(compositeColor(color, background), [background], isDark, minRatio)

export const TEXT_COLOR_SETTINGS = {
  light: { minContrast: 4.5, strongContrast: 4.5 },
  dark: { minContrast: 4.5, strongContrast: 4.5 },
} as const

// No desatura ni mezcla con gris. Solo corrige un contraste insuficiente.
// No modifica main, light, dark ni los colores de los controles.
export const buildReadableTextColor = (
  color: string,
  backgrounds: readonly string[],
  isDark: boolean,
  minRatio: number = TEXT_COLOR_SETTINGS[isDark ? 'dark' : 'light'].minContrast,
): string => {
  return fitContrast(color, backgrounds, isDark, minRatio)
}

export const getBestContrastColor = (background: string, _isDark?: boolean): string =>
  contrastRatio('#ffffff', background) >= contrastRatio('#000000', background) ? '#ffffff' : '#000000'

const stateCache = new Map<string, Readonly<AdvancedColorStates>>()

export const generateAdvancedStatesFromHex = (
  mainColor: string,
  paperBg: string,
  isDark: boolean,
): Readonly<AdvancedColorStates> => {
  // Consulta antes de interpretar o componer colores repetidos.
  const key = `${mainColor}|${paperBg}|${isDark}`
  const cached = stateCache.get(key)
  if (cached) return cached

  const main = compositeColor(mainColor, paperBg)
  const mode = isDark ? 'dark' : 'light'
  const softBgColor = mixColors(main, paperBg, COLOR_TINTS.soft[mode] / 100)
  const strongBgColor = mixColors(main, paperBg, COLOR_TINTS.strong[mode] / 100)
  const result = Object.freeze({
    softBgColor,
    softTextColor: buildReadableTextColor(main, [softBgColor], isDark),
    strongBgColor,
    strongTextColor: buildReadableTextColor(
      main,
      [strongBgColor],
      isDark,
      TEXT_COLOR_SETTINGS[mode].strongContrast,
    ),
    hoverBgColor: withAlpha(main, isDark ? 0.08 : 0.04),
    faintBgColor: withAlpha(main, isDark ? 0.04 : 0.02),
    borderColor: withAlpha(main, isDark ? 0.3 : 0.2),
    borderSoft: withAlpha(main, isDark ? 0.2 : 0.15),
    borderMedium: withAlpha(main, isDark ? 0.4 : 0.3),
    borderStrong: withAlpha(main, isDark ? 0.7 : 0.6),
  })
  return remember(stateCache, key, result)
}

export const CSS_KEYWORD_COLORS = new Set([
  'transparent',
  'currentcolor',
  'inherit',
  'initial',
  'unset',
  'revert',
  'revert-layer',
  'none',
])

const nonComputableCache = new Map<string, boolean>()

export const isNonComputableCssColor = (value: string): boolean => {
  const cached = nonComputableCache.get(value)
  if (cached !== undefined) return cached
  let result = false
  try {
    parseColor(value)
  } catch {
    result = true
  }
  return remember(nonComputableCache, value, result)
}

const fromStates = (states: Readonly<AdvancedColorStates>): ColorStyles => ({
  bgColor: states.softBgColor,
  textColor: states.softTextColor,
  strongBgColor: states.strongBgColor,
  strongTextColor: states.strongTextColor,
  borderColor: states.borderColor,
  borderSoft: states.borderSoft,
  borderMedium: states.borderMedium,
  borderStrong: states.borderStrong,
  hoverColor: states.hoverBgColor,
  stripedColor: states.faintBgColor,
})

const ADVANCED_STATE_KEYS: readonly (keyof AdvancedColorStates)[] = [
  'softBgColor',
  'softTextColor',
  'strongBgColor',
  'strongTextColor',
  'hoverBgColor',
  'faintBgColor',
  'borderColor',
  'borderSoft',
  'borderMedium',
  'borderStrong',
]

const hasAdvancedStates = (value: unknown): value is AdvancedColorStates => {
  if (!value || typeof value !== 'object') return false
  return ADVANCED_STATE_KEYS.every(
    (key) => key in value && typeof (value as Record<string, unknown>)[key] === 'string',
  )
}

const readPaletteValue = (palette: Palette, path: string): unknown => {
  let value: unknown = palette
  for (const key of path.split('.')) {
    if (!value || typeof value !== 'object' || !Object.prototype.hasOwnProperty.call(value, key)) {
      return undefined
    }
    value = (value as Record<string, unknown>)[key]
  }
  return value
}

export const getColor = (theme: Theme, bgColor?: StyledPaletteColors | string): ColorStyles => {
  const { palette } = theme
  const isDark = palette.mode === 'dark'
  const paper = palette.background.paper
  const neutral = (): ColorStyles => {
    const main = isDark ? '#ffffff' : '#000000'
    return {
      bgColor: mixColors(main, paper, isDark ? 0.05 : 0.03),
      strongBgColor: mixColors(main, paper, isDark ? 0.1 : 0.06),
      textColor: palette.text.primary,
      strongTextColor: palette.text.primary,
      borderColor: palette.divider,
      borderSoft: palette.divider,
      borderMedium: palette.divider,
      borderStrong: palette.divider,
      hoverColor: palette.action.hover,
      stripedColor: withAlpha(main, 0.04),
    }
  }
  if (!bgColor?.trim()) return neutral()

  const input = bgColor.trim()
  const value = readPaletteValue(palette, input)
  if (hasAdvancedStates(value)) return fromStates(value)
  let color = typeof value === 'string' ? value : input
  if (value && typeof value === 'object' && 'main' in value && typeof value.main === 'string') {
    color = value.main
  }
  if (color.toLowerCase() === 'transparent') {
    return { ...neutral(), bgColor: 'transparent', strongBgColor: 'transparent' }
  }

  // Variables, color-mix, OKLCH y colores CSS con nombre se dejan al navegador.
  // El contraste de esta rama requiere comprobar el valor CSS resuelto.
  if (isNonComputableCssColor(color)) {
    if (CSS_KEYWORD_COLORS.has(color.toLowerCase())) return neutral()
    const mode = isDark ? 'dark' : 'light'
    return {
      ...neutral(),
      bgColor: `color-mix(in srgb, ${color} ${COLOR_TINTS.soft[mode]}%, ${paper})`,
      strongBgColor: `color-mix(in srgb, ${color} ${COLOR_TINTS.strong[mode]}%, ${paper})`,
    }
  }
  return fromStates(generateAdvancedStatesFromHex(color, paper, isDark))
}
